import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, ArrowLeft, Phone, User, Clock, ChevronRight, Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedPhone) fetchMessages(selectedPhone);
  }, [selectedPhone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sms/conversations`);
      setConversations(res.data);
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const res = await axios.get(`${API}/sms/conversations/${encodeURIComponent(phone)}`);
      setMessages(res.data);
      // Refresh conversation list to update unread counts
      const convRes = await axios.get(`${API}/sms/conversations`);
      setConversations(convRes.data);
    } catch {
      toast.error('Failed to load messages');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPhone) return;

    setSending(true);
    try {
      await axios.post(`${API}/sms/send`, {
        phone: selectedPhone,
        message: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages(selectedPhone);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const selectedConversation = conversations.find(c => c.phone === selectedPhone);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col" data-testid="messages-page">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-white">
        <div className="flex items-center gap-3">
          {selectedPhone && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSelectedPhone(null)}
              data-testid="messages-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <MessageSquare className="h-6 w-6 text-[#4B2D6E]" />
          <div>
            <h1 className="text-2xl font-bold text-[#4B2D6E] font-[Lato]">Messages</h1>
            <p className="text-sm text-[#666]">Two-way SMS conversations with candidates</p>
          </div>
        </div>
      </div>

      {/* Main content - split panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation List */}
        <div className={`w-full lg:w-96 border-r bg-white flex flex-col ${selectedPhone ? 'hidden lg:flex' : 'flex'}`}>
          {conversations.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <Inbox className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-[#4B2D6E]">No conversations yet</h3>
                <p className="text-sm text-[#666] mt-1">
                  When candidates reply to SMS notifications, their messages will appear here.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
              {conversations.map((conv) => (
                <button
                  key={conv.phone}
                  onClick={() => setSelectedPhone(conv.phone)}
                  className={`w-full text-left px-4 py-3 border-b transition-colors hover:bg-gray-50 ${
                    selectedPhone === conv.phone ? 'bg-[#F3EAFA] border-l-4 border-l-[#4B2D6E]' : ''
                  }`}
                  data-testid={`conversation-${conv.phone}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#4B2D6E] flex items-center justify-center text-white font-bold text-sm">
                        {conv.candidate_name ? conv.candidate_name.charAt(0).toUpperCase() : <Phone className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#4B2D6E] truncate">
                          {conv.candidate_name || conv.phone}
                        </p>
                        {conv.candidate_name && (
                          <p className="text-xs text-[#999] truncate">{conv.phone}</p>
                        )}
                        <p className="text-sm text-[#666] truncate mt-0.5">
                          {conv.last_direction === 'outbound' && <span className="text-[#999]">You: </span>}
                          {conv.last_message}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                      <span className="text-xs text-[#999]">{formatTime(conv.last_date)}</span>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-[#E8722A] text-white text-xs h-5 min-w-5 flex items-center justify-center rounded-full">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message Thread */}
        <div className={`flex-1 flex flex-col bg-[#F5F5F5] ${!selectedPhone ? 'hidden lg:flex' : 'flex'}`}>
          {!selectedPhone ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-[#4B2D6E]">Select a conversation</h3>
                <p className="text-sm text-[#666] mt-1">Choose a conversation from the left to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div className="px-4 py-3 bg-white border-b flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#4B2D6E] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {selectedConversation?.candidate_name
                    ? selectedConversation.candidate_name.charAt(0).toUpperCase()
                    : <Phone className="h-4 w-4" />}
                </div>
                <div>
                  <p className="font-semibold text-[#4B2D6E]">
                    {selectedConversation?.candidate_name || selectedPhone}
                  </p>
                  <p className="text-xs text-[#666]">
                    {selectedPhone}
                    {selectedConversation?.job_title && ` · ${selectedConversation.job_title}`}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="message-thread">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        msg.direction === 'outbound'
                          ? 'bg-[#4B2D6E] text-white rounded-br-md'
                          : 'bg-white text-[#333] border rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                      <div className={`flex items-center gap-1 mt-1 ${
                        msg.direction === 'outbound' ? 'text-white/60' : 'text-[#999]'
                      }`}>
                        <Clock className="h-3 w-3" />
                        <span className="text-xs">{formatFullTime(msg.created_at)}</span>
                        {msg.sent_by && (
                          <span className="text-xs ml-1">· {msg.sent_by}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <form onSubmit={sendReply} className="px-4 py-3 bg-white border-t flex gap-2" data-testid="message-reply-form">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1"
                  data-testid="message-input"
                  disabled={sending}
                />
                <Button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="bg-[#E8722A] hover:bg-[#D6621F]"
                  data-testid="message-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
